/**
 * Schema & data formatting
 */
import clone from 'clone';
import { AJV_EXTENSION_REF_SCHEMA } from '../../globals';
import { ExtractTSTypeFromType } from '../extract';
import { ISchemaObjectInputType } from '../properties/composite/nested/object';
import { dateToString } from './common';

/**
 * Data formatting: Transform input data into AJV/JSONSchema-compliant input
 * - e.g. no Date objects: must be strings
 */
export function formatDataBeforeValidation<DataType extends ExtractTSTypeFromType>(
    input_data: DataType
): DataType {
    if (!input_data || typeof input_data !== 'object') {
        return input_data;
    } else if (input_data instanceof Date) {
        return input_data.toISOString() as DataType;
    } else {
        const data = clone(input_data);

        if (Array.isArray(input_data)) {
            // @ts-expect-error: Typing!
            return data.map(item => formatValueBeforeValidation(item));
        } else {
            for (const field in data) {
                data[field] = formatValueBeforeValidation(data[field]);
            }

            return data;
        }
    }
}

/**
 * Convert a property value; if an object, keep digging
 * - Must use `any` type to overcome Typescript's concern with recursion; it's not an issue
 */
function formatValueBeforeValidation(input_value: any): any {
    /**
     * 1: Process value, which will convert to string
     */
    const value = conditionalDateToString(input_value);

    /**
     * 2: If still an object, process
     */
    if (Array.isArray(value)) {
        return value.map(v => formatValueBeforeValidation(v));
    } else if (value && typeof value === 'object') {
        return formatDataBeforeValidation(value);
    }

    return value;
}

/**
 * Convert a Date to a string
 */
export function conditionalDateToString(value: any): any {
    if (value instanceof Date) {
        return dateToString(value);
    }

    return value;
}

/**
 * Data re-formatting, post validation
 * - String back to Date, etc.
 */
export function reformatDataAfterValidation<DataType extends ISchemaObjectInputType<any, string>>(
    input_data: DataType
): DataType {
    if (!input_data) {
        return input_data;
    } else if (typeof input_data !== 'object') {
        return reformatValueAfterValidation(input_data);
    } else if (Array.isArray(input_data)) {
        const data = clone(input_data);

        // @ts-expect-error: Typing!
        return data.map(item => reformatValueAfterValidation(item));
    } else {
        const data = clone(input_data);

        for (const field in data) {
            data[field] = reformatValueAfterValidation(data[field]);
        }

        return data;
    }
}

function reformatValueAfterValidation(input_value: any): any {
    /**
     * 1: Process value, which will convert to string
     */

    //  Date
    const value = conditionalStringToDate(input_value);

    //  $ref
    if (input_value?.$ref) {
        return input_value[AJV_EXTENSION_REF_SCHEMA];
    }

    /**
     * 2: If still an object, process
     */
    if (Array.isArray(value)) {
        return value.map(reformatValueAfterValidation);
    } else if (value && typeof value === 'object') {
        return reformatDataAfterValidation(value);
    }

    return value;
}

/**
 * Convert a string back to a Date
 * - If the ISO string matches the input value, we can convert to Date()
 */
export function conditionalStringToDate(value: any): any {
    const date = new Date(value);

    //  Valid?
    if (!isNaN(date.getTime())) {
        // String match?
        if (date.toISOString() === value) {
            return date;
        }
    }

    return value;
}
